/**
 * 门店数据（仅缓存）：订单 + 业务报告，来自 GET /api/store-revenue/store-data-from-cache
 * 供订单流速、RevPAR、房态健康、客源、折扣、渠道分析/信息、出租率、运营效率等组件取数
 *
 * 加载策略：先读 localStorage，若有有效缓存则立即展示；再检查是否过期，仅对已过期的数据发起后台刷新
 * 有缓存时后台刷新不显示 loading 动画，数据更新后组件自动响应
 */
import { useState, useCallback, useEffect } from 'react';
import { getStoreDataFromCacheFromBackend } from '../../service/storeRevenueApi';
import { getStoreDataFromCacheFromLocalStorage, STORE_DATA_FROM_CACHE_TTL_MS, } from '../utils/storeRevenueLocalStorage';
function getInitialFromLocalStorage(storeId) {
    var cached = getStoreDataFromCacheFromLocalStorage(storeId);
    return cached ? cached.data : null;
}
export function useStoreDataFromCache(storeId, options) {
    var enabled = (options === null || options === void 0 ? void 0 : options.enabled) !== false;
    var _a = useState(function () {
        return enabled ? getInitialFromLocalStorage(storeId) : null;
    }), data = _a[0], setData = _a[1];
    var _b = useState(function () {
        if (!enabled)
            return false;
        var cached = getStoreDataFromCacheFromLocalStorage(storeId);
        return !cached;
    }), loading = _b[0], setLoading = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    var _d = useState(0), refreshKey = _d[0], setRefreshKey = _d[1];
    var refresh = useCallback(function () {
        setRefreshKey(function (k) { return k + 1; });
    }, []);
    useEffect(function () {
        if (!enabled) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }
        var cached = getStoreDataFromCacheFromLocalStorage(storeId);
        var hasCache = !!cached;
        var isExpired = hasCache && cached && Date.now() - cached.fetchedAt >= STORE_DATA_FROM_CACHE_TTL_MS;
        var shouldFetch = !hasCache || isExpired || refreshKey > 0;
        if (hasCache && cached) {
            setData(cached.data);
            setLoading(false);
        }
        if (!shouldFetch)
            return;
        var cancelled = false;
        if (!hasCache)
            setLoading(true);
        setError(null);
        getStoreDataFromCacheFromBackend(storeId)
            .then(function (res) {
            if (!cancelled) {
                setData(res);
                setError(null);
            }
        })
            .catch(function (err) {
            if (!cancelled) {
                setError(err instanceof Error ? err : new Error(String(err)));
                if (!hasCache)
                    setData(null);
            }
        })
            .finally(function () {
            if (!cancelled)
                setLoading(false);
        });
        return function () {
            cancelled = true;
        };
    }, [storeId, refreshKey, enabled]);
    return { data: data, loading: loading, error: error, refresh: refresh };
}
